import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { styled } from "@mui/material/styles";
import {
  TypographyLarge,
  TypographyNormal,
  TypographySmall,
} from "../../customComponent";
import {
  CloseConfirmationDialog,
  openCancelDialog,
} from "../../global/CloseConfirmationDialog";
import { inputOnlyNumbers } from "../../../helpers/inputProcessing";
import { logger } from "../../../helpers/logger";
import { defaultHTML } from "./htmlDefault";
import { v4 as uuidv4 } from "uuid";
import parser from "html-react-parser";
import Editor from "@monaco-editor/react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { colorStyling } from "../../../helpers/color";

const StyledBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  ":last-child": {
    marginBottom: 0,
  },
}));

const htmlFilesAcceptTypes = ["html", "htm"];

const HTMLComponentSetting = ({
  open,
  handleClose,
  addContent,
  container,
  editContent,
  saveEditChanges,
  assetCount,
}) => {
  const dispatch = useDispatch();
  const { layerMedias } = useSelector((state) => state.cms);

  const { t } = useTranslation("cms");
  const { t: tCommon } = useTranslation();

  const [assetIndex, setAssetIndex] = useState(null);
  const [assetName, setAssetName] = useState("");
  const [assetDuration, setAssetDuration] = useState(10);
  const [htmlEditor, setHtmlEditor] = useState(defaultHTML);
  const [editedFlag, setEditedFlag] = useState(false);

  const [openNewHTMLDialog, setOpenNewHTMLDialog] = useState(false);
  const [openViewerDialog, setOpenViewerDialog] = useState(false);
  const [errorNameFlag, setErrorNameFlag] = useState(false);
  const [errorDurationFlag, setErrorDurationFlag] = useState(false);

  useEffect(() => {
    if (open) {
      setAssetIndex(null);
      setAssetName("");
      setAssetDuration(10);
      setHtmlEditor(defaultHTML);
      setOpenViewerDialog(false);
      setErrorNameFlag(false);
      setErrorDurationFlag(false);

      logger.log(`HTMLComponentSetting opened!`, { editContent });
      if (editContent) {
        const index = layerMedias[container.id].findIndex(
          (media) => media.id === editContent.id
        );

        setAssetIndex(index);
        setAssetName(editContent.name);
        setAssetDuration(editContent.interval);
        setHtmlEditor(editContent.html);
      }
    } else {
      setEditedFlag(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleHtmlEditorChange = (value, event) => {
    setHtmlEditor(value);
    setEditedFlag(true);
  };

  const handleAssetNameChange = (e) => {
    setErrorNameFlag(false);
    setAssetName(e.target.value);
    setEditedFlag(true);
  };

  const handleAssetDurationChange = (e) => {
    setErrorDurationFlag(false);
    setAssetDuration(e.target.value);
    setEditedFlag(true);
  };

  const handleOpenNewHTMLDialog = () => {
    setOpenNewHTMLDialog(true);
  };

  const handleCloseNewHTMLDialog = () => {
    setOpenNewHTMLDialog(false);
  };

  const handleOpenViewerDialog = () => {
    setOpenViewerDialog(true);
  };

  const handleCloseViewerDialog = () => {
    setOpenViewerDialog(false);
  };

  const handleCloseDialog = () => {
    handleClose();
  };

  const handleOpenCancelDialog = () => {
    openCancelDialog(dispatch, editedFlag, handleCloseDialog);
  };

  const handleSubmitHTMLContent = () => {
    const shortUUID = uuidv4();
    const addedContent = {
      id: shortUUID,
      interval: parseInt(assetDuration),
      type: "HTML",
      appType: "html",
      name: assetName,
      html: htmlEditor,
      editFlag: true,
    };

    logger.log(`handleSubmitHTMLContent:`, addedContent);
    const updatedMedias = {};
    const updatedOrder = [];
    updatedMedias[addedContent.id] = addedContent;
    updatedOrder.push(addedContent.id);
    addContent(updatedMedias, updatedOrder);
  };

  const handleSaveEdit = () => {
    const edited = {
      id: editContent.id,
      interval: parseInt(assetDuration),
      type: "HTML",
      appType: "html",
      name: assetName,
      html: htmlEditor,
      editFlag: true,
    };

    logger.log(`handleSaveEdit:`, edited);
    saveEditChanges(assetIndex, edited);
  };

  const submitDialog = () => {
    let errorCount = 0;
    if (assetName.length < 4) {
      setErrorNameFlag(true);
      errorCount++;
    }
    if (parseInt(assetDuration) === 0) {
      setErrorDurationFlag(true);
      errorCount++;
    }
    if (errorCount > 0) {
      return;
    }

    if (!editContent) {
      handleSubmitHTMLContent();
    } else {
      handleSaveEdit();
    }
    handleCloseDialog();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileName = file.name;
      const fileExt = fileName.split(".").pop();
      if (htmlFilesAcceptTypes.includes(fileExt)) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const fileContent = e.target.result;
          setHtmlEditor(fileContent);
        };

        reader.readAsText(file);
        setEditedFlag(true);
      } else {
        await withReactContent(Swal).fire({
          title: tCommon("errorLabel"),
          html: t("htmlComponent.uploadWrongTypeSwalText"),
          icon: "warning",
          confirmButtonColor: colorStyling.cms.blue,
          confirmButtonText: tCommon("okBtn"),
          customClass: { container: "swal-dialog" },
        });
      }
    }
  };

  const parseContentSubmitText = useMemo(() => {
    if (!editContent) {
      return tCommon("submitBtn");
    } else {
      return tCommon("saveBtn");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editContent]);

  const enableDuration = useMemo(() => {
    if (assetCount > 1) {
      return true;
    } else if (assetCount === 1) {
      return !editContent ? true : false;
    } else {
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetCount, editContent]);

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown={true}
      onClose={() => handleCloseDialog()}
    >
      <CloseConfirmationDialog handleCancel={handleClose} />

      <DialogTitle
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        {t("htmlComponent.title")}
        <IconButton onClick={() => handleCloseDialog()}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ minHeight: "33vh" }}>
        <HTMLViewer
          open={openViewerDialog}
          handleClose={handleCloseViewerDialog}
          html={htmlEditor}
        />

        <Box
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
          sx={{ mt: 1, px: 3 }}
        >
          <Box
            display={"flex"}
            justifyContent={"left"}
            alignItems={"center"}
            sx={{
              py: 2,
              px: 4,
              width: "100%",
              border: "1px solid black",
              borderRadius: "22px",
            }}
          >
            <Box
              width={"100%"}
              display={"flex"}
              flexDirection={"column"}
              gap={1}
            >
              <StyledBox>
                <input
                  accept=".html,.htm"
                  style={{ display: "none" }}
                  id="upload-html-file"
                  type="file"
                  onChange={handleFileUpload}
                />

                <Box display={"flex"} alignItems={"center"} sx={{ mb: 1 }}>
                  <TypographyLarge sx={{ ml: 1 }}>
                    {t("htmlComponent.editorLabel")}
                  </TypographyLarge>
                </Box>

                <Box
                  width={"100%"}
                  display={"flex"}
                  flexDirection={"column"}
                  alignItems={"center"}
                  sx={{
                    border: "1px solid black",
                  }}
                >
                  <Box
                    width={"100%"}
                    display={"flex"}
                    alignItems={"center"}
                    sx={{
                      height: "25px",
                      borderBottom: "1px solid black",
                    }}
                  >
                    <Button
                      size="small"
                      disableRipple={false}
                      onClick={handleOpenNewHTMLDialog}
                      sx={{
                        borderRadius: 0,
                        borderRight: "1px solid black",
                      }}
                    >
                      <Box sx={{ px: 2 }}>
                        <TypographySmall
                          sx={{ color: "black", textAlign: "center" }}
                        >
                          {t("htmlComponent.newBtn")}
                        </TypographySmall>
                      </Box>
                    </Button>
                    <label htmlFor="upload-html-file">
                      <Button
                        size="small"
                        disableRipple={false}
                        component="span"
                        sx={{
                          borderRadius: 0,
                          borderRight: "1px solid black",
                        }}
                      >
                        <Box sx={{ px: 2 }}>
                          <TypographySmall
                            sx={{ color: "black", textAlign: "center" }}
                          >
                            {t("htmlComponent.uploadBtn")}
                          </TypographySmall>
                        </Box>
                      </Button>
                    </label>
                    <Button
                      size="small"
                      disableRipple={false}
                      onClick={handleOpenViewerDialog}
                      sx={{
                        borderRadius: 0,
                        borderRight: "1px solid black",
                      }}
                    >
                      <Box sx={{ px: 2 }}>
                        <TypographySmall
                          sx={{ color: "black", textAlign: "center" }}
                        >
                          {t("htmlComponent.viewBtn")}
                        </TypographySmall>
                      </Box>
                    </Button>
                    <Box width={"60%"} sx={{ px: 2 }}>
                      <TypographySmall
                        sx={{ color: "black", textAlign: "center" }}
                      >
                        {`${assetName}.html`}
                      </TypographySmall>
                    </Box>
                  </Box>

                  <Editor
                    height={"33vh"}
                    defaultLanguage="html"
                    value={htmlEditor}
                    onChange={handleHtmlEditorChange}
                    options={{}}
                  />
                </Box>
              </StyledBox>

              <StyledBox>
                <Box display={"flex"} gap={"3px"} alignItems={"center"}>
                  <TypographyLarge sx={{ ml: 1 }}>
                    {t("htmlComponent.nameLabel")}
                  </TypographyLarge>
                </Box>

                <Box width={"50%"} display={"flex"} flexDirection={"column"}>
                  <TextField
                    margin="dense"
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder={t("htmlComponent.namePlaceholder")}
                    value={assetName}
                    onChange={handleAssetNameChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          sx={{ ml: "10px", fontWeight: 600 }}
                        >
                          .{`html`}
                        </InputAdornment>
                      ),
                    }}
                    sx={{ pl: 2 }}
                  />

                  {errorNameFlag && (
                    <TypographySmall
                      sx={{
                        ml: 2,
                        mt: "2px",
                        color: "red",
                        fontWeight: 600,
                      }}
                    >
                      {t("htmlComponent.nameErrorHint")}
                    </TypographySmall>
                  )}
                </Box>
              </StyledBox>

              <StyledBox>
                <Box display={"flex"} gap={"3px"} alignItems={"center"}>
                  <TypographyLarge sx={{ ml: 1 }}>
                    {t("htmlComponent.durationLabel")}
                  </TypographyLarge>
                  <Tooltip
                    arrow
                    disableInteractive
                    placement="top"
                    title={
                      <TypographySmall
                        sx={{ color: "white", textAlign: "center" }}
                      >
                        {t("htmlComponent.durationTip")}
                      </TypographySmall>
                    }
                  >
                    <InfoOutlinedIcon fontSize="10px" />
                  </Tooltip>
                </Box>

                <Box width={"50%"} display={"flex"} flexDirection={"column"}>
                  <Box
                    width={"100%"}
                    display={"flex"}
                    alignItems={"center"}
                    gap={1}
                  >
                    <Tooltip
                      arrow
                      disableInteractive
                      title={
                        enableDuration ? (
                          ""
                        ) : (
                          <TypographySmall
                            sx={{ color: "white", textAlign: "center" }}
                          >
                            {t("setting.durationDisabledTip")}
                          </TypographySmall>
                        )
                      }
                    >
                      <TextField
                        margin="dense"
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={assetDuration}
                        disabled={!enableDuration}
                        onChange={(e) => handleAssetDurationChange(e)}
                        onKeyDown={(e) => inputOnlyNumbers(e)}
                        sx={{ pl: 2 }}
                      />
                    </Tooltip>
                    <TypographyNormal flex={"none"}>
                      {t("setting.durationHint")}
                    </TypographyNormal>
                  </Box>

                  {errorDurationFlag && (
                    <TypographySmall
                      sx={{
                        ml: 2,
                        mt: "2px",
                        color: "red",
                        fontWeight: 600,
                      }}
                    >
                      {t("setting.durationErrorHint")}
                    </TypographySmall>
                  )}
                </Box>
              </StyledBox>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ mx: 2, mb: 1 }}>
        <>
          <Button variant="outlined" onClick={handleOpenCancelDialog}>
            {tCommon("cancelBtn")}
          </Button>
          <Button variant="contained" onClick={() => submitDialog()}>
            {parseContentSubmitText}
          </Button>
        </>
      </DialogActions>

      <Dialog maxWidth="sm" open={openNewHTMLDialog}>
        <DialogContent>
          <DialogContentText
            sx={{
              mb: 1,
              textAlign: "center",
            }}
          >
            {parser(t("htmlComponent.newConfirmationText"))}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleCloseNewHTMLDialog}
          >
            {tCommon("noBtn")}
          </Button>

          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              handleCloseNewHTMLDialog();
              setHtmlEditor(defaultHTML);
            }}
          >
            {tCommon("yesBtn")}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

const HTMLViewer = ({ open, handleClose, html }) => {
  const { t } = useTranslation("cms");
  const { t: tCommon } = useTranslation();

  const handleCloseDialog = () => {
    handleClose();
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={true}
      onClose={() => handleCloseDialog()}
    >
      <DialogTitle
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        {t("htmlComponent.viewerTitle")}
        <IconButton onClick={() => handleCloseDialog()}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ minHeight: "40vh" }}>
        <Box display={"flex"} flexDirection={"column"} alignItems={"center"}>
          <Box
            width={"100%"}
            display={"flex"}
            alignItems={"center"}
            sx={{
              border: "1px solid black",
            }}
          >
            <iframe
              title="preview"
              srcDoc={html} // or sanitizedHTML if already wrapped
              // sandbox="allow-same-origin" // Security: restrict iframe capabilities
              style={{
                width: "100%",
                minHeight: "55vh",
                border: "1px solid #ccc",
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ mx: 2, mb: 1 }}>
        <>
          <Button variant="outlined" onClick={() => handleCloseDialog()}>
            {tCommon("closeBtn")}
          </Button>
        </>
      </DialogActions>
    </Dialog>
  );
};

export default HTMLComponentSetting;
